<template>
  <section
    v-tooltip.bottom="!label ? t('notification', 2) : ''"
    v-bind="$attrs"
    class="bg-transparent flex items-center rounded-lg cursor-pointer"
    :class="[size, justify]"
    @click="usePopoverToggle(popover, $event)"
  >
    <div class="flex items-center gap-2">
      <HealthBadge
        v-if="!label"
        :value="notificationsNotRead?.totalNotRead ?? 0"
        bg-color="bg-white"
        text-color="text-primary-800 font-bold"
        text-size="text-[10px]"
        top="-top-2.5"
      >
        <Icon icon="mdi:bell" class="text-yellow-400" :class="iconClass" />
      </HealthBadge>

      <template v-else>
        <Icon icon="mdi:bell" class="text-yellow-400" :class="iconClass" />
        <div class="flex items-center gap-1.5">
          <p :class="labelClass">{{ t("notification", 2) }}</p>
          <span
            v-if="notificationsNotRead?.totalNotRead"
            class="rounded-full flex items-center justify-center bg-primary-500 dark:bg-primary-600 text-white font-bold w-[20px] h-[20px] text-[10px]"
          >
            {{ notificationsNotRead?.totalNotRead }}
          </span>
        </div>
      </template>
    </div>
  </section>

  <HealthPopover ref="popover" class="!bg-gray-50 dark:!bg-dark-500 max-w-sm w-full" padding="!p-0">
    <section
      class="px-4 py-4 flex flex-row items-center justify-between border-b border-gray-300 dark:border-gray-600"
    >
      <h1 class="text-lg">{{ t("notification", 2) }}</h1>
      <div class="flex flex-row gap-2 items-center">
        <p
          class="text-gray-600 dark:text-gray-100 font-medium text-xs hover:underline underline-offset-4 cursor-pointer"
          :class="{ 'animate-pulse [animation-duration:1s]': markAllNotificationsLoading }"
          @click="markAllReadNotification()"
        >
          {{ t("mark_all_as_read") }}
        </p>

        <HealthButton
          v-tooltip.top="t('close')"
          text-color="text-black dark:text-white"
          color="transparent"
          size="p-0"
          icon="famicons:close-sharp"
          @click="usePopoverHide(popover, $event)"
        />
      </div>
    </section>

    <section class="flex flex-col px-4 max-h-72 overflow-y-auto">
      <template v-if="notificationsList.length">
        <div v-for="(notification, idx) in notificationsList" :key="idx">
          <div v-if="loading" class="py-4">
            <HealthSkeleton class="!bg-gray-300 dark:!bg-gray-700 !h-[4rem]" />
          </div>
          <div v-else class="flex flex-row justify-between gap-2 py-4 items-start">
            <div class="flex flex-col gap-1">
              <p class="text-xs text-gray-900 dark:text-gray-50 font-medium">
                {{ notification.message }}
              </p>
              <p class="text-[11px] text-gray-500 dark:text-gray-300 font-medium">
                {{ stringToDateTime(notification.createdAt) }}
              </p>
            </div>
            <HealthButton
              v-tooltip.top="notification.read ? t('read') : t('mark_as_read')"
              :text-color="
                notification.read
                  ? 'text-primary-500 dark:text-primary-50'
                  : 'text-black dark:text-white'
              "
              color="transparent"
              size="p-0"
              :cursor="notification.read ? '!cursor-default' : '!cursor-pointer'"
              :icon="notification.read ? 'ic:round-check-circle' : 'tabler:circle'"
              :loading="markNotificationsLoading && notification.id === notificationId"
              @action="notification.read ? null : markReadNotification(notification.id)"
            />
          </div>
        </div>

        <section class="flex justify-center">
          <p
            v-if="notificationsList.length < totalElements"
            class="text-gray-600 dark:text-gray-100 font-medium text-sm pb-3 hover:underline underline-offset-4 cursor-pointer"
            @click="seeMore"
          >
            {{ t("see_more") }}
          </p>
        </section>
      </template>
      <template v-else>
        <section class="flex flex-col items-center gap-1 py-4">
          <img
            src="@/assets/notification/nothing_notification.svg"
            alt="Nothing notification"
            class="h-[10rem]"
          />
          <p class="text-sm text-gray-500 dark:text-gray-300 font-semibold">
            {{ t("all_good_here_no_new_notifications") }}
          </p>
        </section>
      </template>
    </section>
  </HealthPopover>
</template>

<script setup lang="ts">
  import { useI18n } from "vue-i18n";
  import { onMounted, ref } from "vue";
  import { usePopoverHide, usePopoverToggle } from "@/shared/composables/usePopover";
  import { useDateFormatter } from "@/shared/composables/useDateFormatter";
  import { getAllPaginatedNotificationsController } from "@/app/header/notifications/controllers/getAllPaginatedNotificationsController";
  import { useForm } from "@/shared/composables/form/useForm";
  import { markAllNotificationsController } from "@/app/header/notifications/controllers/markAllNotificationsController";
  import { markNotificationsController } from "@/app/header/notifications/controllers/markNotificationsController";
  import { getNotificationsNotReadController } from "@/app/header/notifications/controllers/getNotificationsNotReadController";
  import { storeToRefs } from "pinia";
  import { useAuth } from "@/shared/stores/auth";
  import { Icon } from "@iconify/vue";
  import { NotificationsFilterModel } from "@/app/header/notifications/models/NotificationsFilterModel";
  import HealthBadge from "@/shared/components/utils/HealthBadge.vue";
  import HealthPopover from "@/shared/components/utils/HealthPopover.vue";
  import HealthButton from "@/shared/components/utils/button/HealthButton.vue";
  import HealthSkeleton from "@/shared/components/utils/HealthSkeleton.vue";
  import { useIntervalFn } from "@vueuse/core";

  const { user } = storeToRefs(useAuth());

  const { t } = useI18n();

  const { stringToDateTime } = useDateFormatter();

  const popover = ref();

  const notificationId = ref<number>();

  defineProps({
    label: {
      type: Boolean,
      default: false,
    },
    labelClass: {
      type: String,
      default: "block font-semibold uppercase ml-1",
    },
    justify: {
      type: String,
      default: "justify-center",
    },
    size: {
      type: String,
      default: "py-2.5 px-2.5",
    },
    iconClass: {
      type: String,
      default: "min-w-[1.2rem] min-h-[1.2rem] md:min-w-[1.6rem] md:min-h-[1.6rem]",
    },
  });

  const { modelClass: notificationsFilterModel } = useForm(NotificationsFilterModel);

  const {
    request: getAllPaginatedNotifications,
    data: notifications,
    loading,
  } = getAllPaginatedNotificationsController();

  const { request: getNotificationsNotRead, data: notificationsNotRead } =
    getNotificationsNotReadController();

  const { request: markAllNotifications, loading: markAllNotificationsLoading } =
    markAllNotificationsController();

  const { request: markNotifications, loading: markNotificationsLoading } =
    markNotificationsController();

  const notificationsList = ref([]);

  const totalElements = ref(0);

  const page = ref(0);

  onMounted(loadPage);

  async function loadPage() {
    await getNotificationsNotRead(user.value.id);

    notificationsFilterModel.value.page = page.value;

    await getAllPaginatedNotifications(user.value.id, notificationsFilterModel.value).then(
      async () => {
        totalElements.value = notifications.value?.totalElements;

        if (page.value === 0) {
          notificationsList.value = notifications.value?.content;
        } else {
          notificationsList.value = [...notificationsList.value, ...notifications.value?.content];
        }
      },
    );
  }

  function seeMore() {
    if (notificationsList.value.length < totalElements.value) {
      page.value++;
      loadPage();
    }
  }

  async function markReadNotification(_notificationId: number) {
    notificationId.value = _notificationId;

    await markNotifications(user.value.id, _notificationId).then(async () => {
      const item = notificationsList.value.find((n) => n.id === _notificationId);
      if (item) item.read = true;
      await getNotificationsNotRead(user.value.id);
    });
  }

  async function markAllReadNotification() {
    await markAllNotifications(user.value.id).then(async () => {
      notificationsList.value.forEach((n) => (n.read = true));

      await getNotificationsNotRead(user.value.id);
    });
  }

  useIntervalFn(() => loadPage(), 2 * 60 * 1000);
</script>
